/* eslint-disable camelcase */
import { useState, useEffect, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Grid, Typography, Autocomplete, Badge, Button } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import StorefrontIcon from "@mui/icons-material/Storefront"
import { GlobalContext } from "../../../contexts/GlobalContext"
import { useQueryClient } from "@tanstack/react-query"
import Swal from "sweetalert2"
import JsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import dayjs from "dayjs"

// Importamos los componentes desde la carpeta Crear de Egresos
import CatalogoProductos from "../Crear/components/CatalogoProductos"
import CarritoResumen from "../Crear/components/CarritoResumen"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarSolicitudEgresoCliente = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const queryClient = useQueryClient()
  const globalContext = useContext(GlobalContext)

  const [carritoOpen, setCarritoOpen] = useState(false)
  const fechaHoy = new Date().toISOString().split("T")[0]

  // Paso 1: Estado unificado ampliado con Agencias y Contactos
  const [formData, setFormData] = useState({
    sgasolegr: "",
    sgastatus: "PENDIENTE",
    tsolcodigo: "",
    tsolInEg: "",
    ciaaproautinsol: 0,
    motcodigo: "",
    sgadescri: "",
    sgacomenenvio: "",
    clicodigo: "",
    procodigo: "",
    sgafecenvio: fechaHoy,
    sgahorenvio: "",
    agencodigosol: "", // Agencia Solicita
    agencodigoenv: "", // Agencia a Enviar
    condescrienv: "", // Contacto Recibe
    detalle: [],
  })

  // Retorno de seguridad si recargan la página y se pierde el ID del state
  useEffect(() => {
    if (!state?.sgasolegr) navigate(-1)
  }, [state, navigate])

  // ==========================================================================
  // 1. OBTENER DATOS DE LA SOLICITUD EXISTENTE
  // ==========================================================================
  const { data: solData, isLoading: loadSol } = useQuery({
    queryKey: ["getSolEgrCliById", state?.sgasolegr],
    queryFn: async () => {
      try {
        const response = await api.post("/CrearSolicitudEgrCli/getSolicitudById", { sgasolegr: state.sgasolegr })

        // Extractor recursivo
        const findSolData = (obj) => {
          if (!obj) return null
          // El backend puede enviar sgasolegr o sgasoling debido a los alias SQL
          if (obj.sgasolegr || obj.sgasoling) return obj
          if (obj.data) return findSolData(obj.data)
          return null
        }

        const coreData = findSolData(response)
        return coreData || {}
      } catch (error) {
        console.error("Error obteniendo la solicitud:", error)
        return null
      }
    },
    enabled: !!state?.sgasolegr,
  })

  // ==========================================================================
  // 2. CARGA DE CATÁLOGOS
  // ==========================================================================
  const { data: catData, isLoading: loadCat } = useQuery({
    queryKey: ["catalogosEgresoClienteEdit"],
    queryFn: async () => {
      try {
        const response = await api.post("/CrearSolicitudEgrCli/getCatalogosEgreso", {})

        const findCatalogos = (obj) => {
          if (!obj) return {}
          if (obj.catalogo_productos || obj.motivos) return obj
          if (obj.data) return findCatalogos(obj.data)
          return {}
        }

        return findCatalogos(response)
      } catch (error) {
        return {
          tipos_solicitud: [],
          motivos: [],
          catalogo_productos: [],
          proveedores: [],
          agencias: [],
          contactos: [],
          cliente_asignado: null,
        }
      }
    },
    staleTime: 0,
  })

  // Extracción Defensiva
  const extractArray = (rawList) =>
    Array.isArray(rawList) ? rawList : Array.isArray(rawList?.data) ? rawList.data : []
  const listaTiposSolicitud = extractArray(catData?.tipos_solicitud)
  const listaMotivos = extractArray(catData?.motivos)
  const listaProveedores = extractArray(catData?.proveedores)
  const listaAgencias = extractArray(catData?.agencias)
  const listaContactosGlobal = extractArray(catData?.contactos)
  const catalogoProductos = extractArray(catData?.catalogo_productos)
  const clienteAsignado = catData?.cliente_asignado || null

  // Filtramos la lista de contactos en tiempo real según la agencia de envío seleccionada
  const listaContactosFiltrada = listaContactosGlobal.filter(
    (c) => String(c.agencodigo) === String(formData.agencodigoenv),
  )

  // ==========================================================================
  // SINCRONIZACIÓN DE DATOS AL FORMULARIO
  // ==========================================================================
  useEffect(() => {
    // Lectura tolerante: Verifica las llaves originales o los alias de SQL Server
    const solId = solData?.sgasolegr || solData?.sgasoling

    if (solData && solId) {
      const rawFechaEnvio = solData.sgafecenvio || solData.sgafecllegada
      const rawHoraEnvio = solData.sgahorenvio || solData.sgahorllegada
      const rawComenEnvio = solData.sgacomenenvio || solData.sgacomenllegada || ""

      const fechaEnvioSQL = rawFechaEnvio ? rawFechaEnvio.split(" ")[0] : fechaHoy

      let horaEnvioSQL = ""
      if (rawHoraEnvio) {
        const partesHora = rawHoraEnvio.split(" ")[1] // Extrae "HH:MM:SS"
        if (partesHora) horaEnvioSQL = partesHora.substring(0, 5) // "HH:MM"
      }

      setFormData({
        sgasolegr: solId,
        sgastatus: solData.sgastatus || "PENDIENTE",
        tsolcodigo: solData.tsolcodigo || "",
        tsolInEg: "",
        ciaaproautinsol: 0,
        motcodigo: solData.motcodigo || "",
        sgadescri: solData.sgadescri || "",
        sgacomenenvio: rawComenEnvio,
        clicodigo: solData.clicodigo || "",
        procodigo: solData.procodigo || "",
        sgafecenvio: fechaEnvioSQL,
        sgahorenvio: horaEnvioSQL,
        agencodigosol: solData.agencodigosol || "",
        agencodigoenv: solData.agencodigoenv || "",
        condescrienv: solData.condescrienv || "",
        detalle: solData.detalle || [],
      })
    }
  }, [solData, fechaHoy])

  useEffect(() => {
    if (clienteAsignado?.id && formData.clicodigo !== clienteAsignado.id && !formData.sgasolegr) {
      setFormData((prev) => ({ ...prev, clicodigo: clienteAsignado.id }))
    }
  }, [clienteAsignado, formData.clicodigo, formData.sgasolegr])

  // REGLA DE NEGOCIO: Bloqueo si no es PENDIENTE
  const isReadOnly = formData.sgastatus !== "PENDIENTE"

  // ==========================================================================
  // 3. MUTACIÓN DE ACTUALIZACIÓN
  // ==========================================================================
  const { mutateAsync: UpdateSolicitud, isPending: isSaving } = useMutation({
    queryKey: ["isUpdatingSolEgrCli"],
    fn: async (data) => {
      const response = await api.post("/CrearSolicitudEgrCli/updateSolicitudEgreso", data)
      return response.data
    },
    showError: "modal",
    showSuccess: false,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries()

      const numeroGenerado = response?.data?.solnumero || response?.solnumero || formData.sgasolegr

      const confirmacion = await Swal.fire({
        title: "¡Solicitud Actualizada!",
        html: `<p>Se ha actualizado exitosamente la Solicitud Nro: <strong>${numeroGenerado}</strong></p><p>¿Desea imprimir el comprobante actualizado?</p>`,
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "Sí, imprimir",
        cancelButtonText: "No, salir",
        confirmButtonColor: "#196C87",
        cancelButtonColor: "#7f8c8d",
        reverseButtons: true,
        allowOutsideClick: false,
      })

      if (confirmacion.isConfirmed) {
        handleImprimirReporte(numeroGenerado)
        navigate(-1)
      } else {
        navigate(-1)
      }
    },
  })

  // Generación PDF (Egreso)
  const handleImprimirReporte = (solnumero) => {
    const doc = new JsPDF("p", "pt", "a4")
    const nombreCiaDinamic =
      globalContext?.ciaSeleccionada?.label ||
      globalContext?.ciaSeleccionada?.cianombre ||
      globalContext?.empresaSeleccionada?.label ||
      globalContext?.seleccion?.cianombre ||
      "SISTEMA LOGÍSTICO MERCATTI"
    const tipoSol = listaTiposSolicitud.find((t) => t.tsolcodigo === formData.tsolcodigo)?.label || ""
    const motivo = listaMotivos.find((m) => m.id === formData.motcodigo)?.label || ""
    const ageSol = listaAgencias.find((a) => a.id === formData.agencodigosol)?.label || ""
    const ageEnv = listaAgencias.find((a) => a.id === formData.agencodigoenv)?.label || ""
    const proveedor = listaProveedores.find((p) => p.id === formData.procodigo)?.label || "N/A"

    const truncateTxt = (txt, max) => (txt.length > max ? txt.substring(0, max) + "..." : txt)

    doc.setFontSize(16)
    doc.setTextColor(25, 108, 135)
    doc.text(nombreCiaDinamic, 40, 40)
    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text(`SOLICITUD DE EGRESO NRO: ${solnumero}`, 40, 65)
    doc.setFontSize(10)
    doc.setTextColor(60, 60, 60)

    doc.text(truncateTxt(`Cliente Asociado: ${clienteAsignado?.label || formData.clicodigo}`, 70), 40, 95)
    doc.text(truncateTxt(`Tipo de Solicitud: ${tipoSol}`, 70), 40, 110)
    doc.text(truncateTxt(`Motivo de Egreso: ${motivo}`, 70), 40, 125)

    doc.text(truncateTxt(`Agencia Solicita: ${ageSol}`, 70), 40, 140)
    doc.text(truncateTxt(`Agencia Envío: ${ageEnv}`, 70), 40, 155)
    doc.text(truncateTxt(`Contacto Recibe: ${formData.condescrienv}`, 70), 40, 170)

    doc.text(
      `Fecha/Hora Envío: ${dayjs(formData.sgafecenvio).format("DD/MM/YYYY")} a las ${formData.sgahorenvio}`,
      555,
      95,
      { align: "right" },
    )
    doc.text(`Fecha Impresión: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 555, 110, { align: "right" })

    doc.setFont("helvetica", "bold")
    doc.text("Descripción General:", 40, 190)
    doc.setFont("helvetica", "normal")
    const splitDesc = doc.splitTextToSize(formData.sgadescri, 500)
    doc.text(splitDesc, 40, 205)

    const tableColumn = ["Ítem", "Código", "Descripción", "Cantidad Solicitada"]
    const tableRows = formData.detalle.map((prod, idx) => [
      idx + 1,
      prod.artcodigo,
      prod.artdescri,
      prod.cantidad_solicitada,
    ])

    autoTable(doc, {
      startY: 220 + splitDesc.length * 10,
      head: [tableColumn],
      body: tableRows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 4, textColor: [40, 40, 40] },
      headStyles: { fillColor: [25, 108, 135], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { halign: "center", cellWidth: 40 },
        1: { halign: "left", cellWidth: 100 },
        3: { halign: "center", cellWidth: 100, fontStyle: "bold" },
      },
    })
    doc.save(`Solicitud_Egreso_${solnumero}.pdf`)
  }

  const handleInputChange = (field, value) => {
    if (isReadOnly) return
    setFormData((prev) => ({ ...prev, [field]: value.toUpperCase() }))
  }

  const handleDateChange = (field, value) => {
    if (isReadOnly) return
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleTipoSolicitudChange = (event, newValue) => {
    const nuevoTipo = newValue ? String(newValue.id) : ""
    const nuevoCodigo = newValue ? String(newValue.tsolcodigo) : ""
    const autoAprob = newValue?.ciaaproautinsol || 0

    setFormData((prev) => ({
      ...prev,
      tsolInEg: nuevoTipo,
      tsolcodigo: nuevoCodigo,
      ciaaproautinsol: autoAprob,
    }))
  }

  const handleCheckout = async () => {
    if (isReadOnly) return showWarning(`La solicitud está en estado ${formData.sgastatus} y no puede ser modificada.`)
    if (!formData.tsolcodigo) return showWarning("Debe seleccionar un Tipo de Solicitud permitido.")
    if (!formData.motcodigo) return showWarning("Debe seleccionar un Motivo para su egreso.")
    if (!formData.agencodigosol) return showWarning("Debe seleccionar la Agencia que Solicita.")
    if (!formData.agencodigoenv) return showWarning("Debe seleccionar la Agencia a la que se Envía.")
    if (!formData.condescrienv) return showWarning("Debe seleccionar el Contacto que Recibe.")
    if (!formData.sgafecenvio || !formData.sgahorenvio) return showWarning("Debe especificar Fecha y Hora de envío.")
    if (!formData.sgadescri.trim()) return showWarning("Agregue una descripción a su solicitud.")
    if (formData.detalle.length === 0) return showWarning("Su carrito de solicitud está vacío.")

    const carritoLimpio = formData.detalle.filter((item) => parseFloat(item.cantidad_solicitada) > 0)
    if (carritoLimpio.length === 0) return showWarning("Todos los productos tienen cantidad 0.")

    await UpdateSolicitud({ ...formData, detalle: carritoLimpio })
  }

  const loadingGlobal = loadCat || loadSol || isSaving
  const totalItemsEnCarrito = formData.detalle.reduce(
    (acc, item) => acc + (parseFloat(item.cantidad_solicitada) > 0 ? 1 : 0),
    0,
  )

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={
              <Badge badgeContent={totalItemsEnCarrito} color="error">
                <ShoppingCartIcon />
              </Badge>
            }
            onClick={() => setCarritoOpen(true)}
            disabled={loadingGlobal}
            sx={{ fontWeight: "bold", borderRadius: 8, px: 4 }}
          >
            {isReadOnly ? "Ver Solicitud" : "Modificar Solicitud"}
          </Button>
        </Box>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "0 30px 30px 30px" }}>
          <StorefrontIcon sx={{ fontSize: 40, color: "#196C87", mr: 1 }} />
          <Typography variant="h4" color="primary" fontWeight="bold">
            Editar Solicitud de Egreso {formData.sgasolegr ? `- ${formData.sgasolegr}` : ""}
          </Typography>
        </div>

        {isReadOnly && (
          <Box display="flex" justifyContent="center" mb={3}>
            <Typography variant="subtitle1" color="error" fontWeight="bold">
              ESTA SOLICITUD ESTÁ EN ESTADO {formData.sgastatus} (MODO SOLO LECTURA)
            </Typography>
          </Box>
        )}

        <CustomBackdrop isLoading={loadingGlobal} />

        <Box sx={StyledRoot}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3, background: "white", mb: 4 }}>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ borderBottom: "2px solid #196C87", pb: 1, mb: 3 }}
            >
              Información de Egreso
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Cliente Asociado *"
                  value={clienteAsignado ? clienteAsignado.label : formData.clicodigo || "Cargando..."}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={isReadOnly || loadingGlobal || listaTiposSolicitud.length === 0}
                  options={listaTiposSolicitud}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaTiposSolicitud.find((c) => String(c.tsolcodigo) === String(formData.tsolcodigo)) ||
                    (formData.tsolcodigo
                      ? { id: formData.tsolInEg, tsolcodigo: formData.tsolcodigo, label: "Cargando..." }
                      : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.tsolcodigo) === String(value?.tsolcodigo)}
                  onChange={handleTipoSolicitudChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Solicitud *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={isReadOnly || loadingGlobal || listaProveedores.length === 0}
                  options={listaProveedores}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaProveedores.find((p) => String(p.id) === String(formData.procodigo)) ||
                    (formData.procodigo ? { id: formData.procodigo, label: "Cargando..." } : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, procodigo: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Proveedor Destino (Opcional)" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={isReadOnly || loadingGlobal}
                  options={listaMotivos}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaMotivos.find((c) => String(c.id) === String(formData.motcodigo)) ||
                    (formData.motcodigo ? { id: formData.motcodigo, label: "Cargando..." } : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, motcodigo: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Motivo de Egreso *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              {/* BLOQUE DE AGENCIAS Y CONTACTOS */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={isReadOnly || loadingGlobal || listaAgencias.length === 0}
                  options={listaAgencias}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaAgencias.find((a) => String(a.id) === String(formData.agencodigosol)) ||
                    (formData.agencodigosol ? { id: formData.agencodigosol, label: formData.agencodigosol } : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, agencodigosol: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Agencia que Solicita *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={isReadOnly || loadingGlobal || listaAgencias.length === 0}
                  options={listaAgencias}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaAgencias.find((a) => String(a.id) === String(formData.agencodigoenv)) ||
                    (formData.agencodigoenv ? { id: formData.agencodigoenv, label: formData.agencodigoenv } : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) => {
                    setFormData((prev) => ({
                      ...prev,
                      agencodigoenv: newVal ? String(newVal.id) : "",
                      condescrienv: "",
                    }))
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Agencia de Envío *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={
                    isReadOnly || loadingGlobal || !formData.agencodigoenv || listaContactosFiltrada.length === 0
                  }
                  options={listaContactosFiltrada}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaContactosFiltrada.find((c) => String(c.id) === String(formData.condescrienv)) ||
                    (formData.condescrienv ? { id: formData.condescrienv, label: formData.condescrienv } : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, condescrienv: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Contacto que Recibe *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de Envío *"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: "es-EC" }}
                  value={formData.sgafecenvio}
                  onChange={(e) => handleDateChange("sgafecenvio", e.target.value)}
                  disabled={isReadOnly || loadingGlobal}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="time"
                  label="Hora de Envío *"
                  InputLabelProps={{ shrink: true }}
                  value={formData.sgahorenvio}
                  onChange={(e) => handleDateChange("sgahorenvio", e.target.value)}
                  disabled={isReadOnly || loadingGlobal}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  disabled={isReadOnly || loadingGlobal}
                  fullWidth
                  label="Descripción / Observación *"
                  value={formData.sgadescri}
                  onChange={(e) => handleInputChange("sgadescri", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  disabled={isReadOnly || loadingGlobal}
                  fullWidth
                  label="Instrucciones para el Envío (Opcional)"
                  value={formData.sgacomenenvio}
                  onChange={(e) => handleInputChange("sgacomenenvio", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Renderizado de Galería Amazon bloqueando si es ReadOnly */}
          <div style={{ pointerEvents: isReadOnly ? "none" : "auto", opacity: isReadOnly ? 0.7 : 1 }}>
            <CatalogoProductos
              catalogo={catalogoProductos}
              carrito={formData.detalle}
              setFormData={setFormData}
              loading={loadCat || loadSol}
            />
          </div>
        </Box>
      </div>

      <CarritoResumen
        open={carritoOpen}
        onClose={() => setCarritoOpen(false)}
        carrito={formData.detalle}
        setFormData={setFormData}
        onCheckout={handleCheckout}
        isSaving={isSaving}
        isReadOnly={isReadOnly}
      />
    </ThemeProvider>
  )
}

export default EditarSolicitudEgresoCliente
